import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { FieldReportFullViewFilterContextType } from 'af-root/context/fieldReportFullViewFilterContext';

import type { ExtendedColorValue } from '@acceligentllc/shared/enums/color';

import LabelWithColor from 'af-components/LabelWithColor';
import AdditionalFilter from 'af-components/Table/Filters/AdditionalFilter/Multiselect';

import * as CrewTypeActions from 'af-actions/crewType';

export interface CrewTypeFilterOption {
	id: number | string;
	label: string;
	color: ExtendedColorValue;
	isInternal: boolean;
}

interface OwnProps {
	onChange: (options: CrewTypeFilterOption[]) => void;
	selected: CrewTypeFilterOption[];
	forBulkModal?: boolean;
	onClear?: () => void;
	autoOpen?: boolean;
	fieldReportFullViewFilterContext: FieldReportFullViewFilterContextType;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const CrewTypeFilter: React.FC<Props> = (props) => {
	const {
		onChange,
		findAllForFieldReportFilter,
		selected,
		forBulkModal,
		onClear,
		autoOpen,
		fieldReportFullViewFilterContext,
	} = props;

	const [options, setOptions] = React.useState<CrewTypeFilterOption[] | null>(null);

	const fetchOptions = React.useCallback(async () => {
		const _options = await findAllForFieldReportFilter();
		const formattedOptions = _options.map((option) => ({
			id: option.id ?? option.name,
			label: option.name,
			color: option.color as ExtendedColorValue,
			isInternal: option.isInternal,
		}));
		setOptions(formattedOptions);
		fieldReportFullViewFilterContext.setCrewTypeOptions(formattedOptions);
	}, [fieldReportFullViewFilterContext, findAllForFieldReportFilter]);

	React.useEffect(() => {
		if (!fieldReportFullViewFilterContext.crewTypeOptions.length) {
			fetchOptions();
		} else {
			setOptions(fieldReportFullViewFilterContext.crewTypeOptions);
		}
	}, [fetchOptions, fieldReportFullViewFilterContext.crewTypeOptions, findAllForFieldReportFilter]);

	const renderOption = React.useCallback((option: CrewTypeFilterOption) => {
		return (
			<LabelWithColor color={option.color} text={option.label} />
		);
	}, []);

	const onValueChange = React.useCallback((_options: CrewTypeFilterOption[]) => {
		onChange(_options);
	}, [onChange]);

	return (
		<AdditionalFilter
			autoOpen={!!autoOpen}
			filterName="Crew Type"
			forBulkModal={!!forBulkModal}
			onChange={onValueChange}
			onRemoveFilter={onClear}
			options={options}
			renderOptionOverride={renderOption}
			selected={selected}
		/>
	);
};

function mapDispatchToProps() {
	return {
		findAllForFieldReportFilter: CrewTypeActions.findAllForCompanyWithInternal,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(CrewTypeFilter);
